/**
 * Admin Org-Management API client. Thin wrappers over the shared `apiFetch`
 * helper (see ./api) mapping to the Elysia /admin module. Two surfaces:
 *   - org self-management ("Danger Zone") for the owner-gated settings page, and
 *   - platform super-admin (cross-org) for the platform admin page.
 */
import type {
  DeleteOrgInput,
  OwnershipCandidate,
  PlatformOrgDto,
  PlatformStatus,
  TransferOwnershipInput,
} from '@fc/core'
import { apiFetch } from './api'

export type {
  DeleteOrgInput,
  OwnershipCandidate,
  PlatformOrgDto,
  PlatformStatus,
  TransferOwnershipInput,
} from '@fc/core'

// ---- Org self-management (Danger Zone) ----
export const listOwnershipCandidates = (): Promise<OwnershipCandidate[]> =>
  apiFetch<OwnershipCandidate[]>('/admin/org/owners-candidates')

export const transferOwnership = (input: TransferOwnershipInput): Promise<{ ok: true }> =>
  apiFetch<{ ok: true }>('/admin/org/transfer-ownership', {
    method: 'POST',
    body: JSON.stringify(input),
  })

export const closeOrg = (): Promise<{ ok: true }> =>
  apiFetch<{ ok: true }>('/admin/org/close', { method: 'POST' })

export const deleteOrg = (input: DeleteOrgInput): Promise<{ ok: true }> =>
  apiFetch<{ ok: true }>('/admin/org/delete', { method: 'POST', body: JSON.stringify(input) })

// ---- Platform super-admin (cross-org) ----
export const getPlatformStatus = (): Promise<PlatformStatus> =>
  apiFetch<PlatformStatus>('/admin/platform/status')

export const listPlatformOrgs = (): Promise<PlatformOrgDto[]> =>
  apiFetch<PlatformOrgDto[]>('/admin/platform/orgs')

export const suspendPlatformOrg = (orgId: string): Promise<PlatformOrgDto> =>
  apiFetch<PlatformOrgDto>(`/admin/platform/orgs/${orgId}/suspend`, { method: 'POST' })

export const activatePlatformOrg = (orgId: string): Promise<PlatformOrgDto> =>
  apiFetch<PlatformOrgDto>(`/admin/platform/orgs/${orgId}/activate`, { method: 'POST' })

export const deprovisionPlatformOrg = (orgId: string): Promise<void> =>
  apiFetch<void>(`/admin/platform/orgs/${orgId}`, { method: 'DELETE' })
